import type { PropertyFiltersApi, PropertySearchScope } from './context';
import type { PropertySearchSnapshot } from './store';
import type { EventBusKey } from '@vueuse/core';

import {
	DEFAULT_PROPERTY_FILTERS_URL,
	getPropertyTypeFromSlug,
	getSlugFromTransactionType,
	getTransactionTypeFromSlug,
	parsePropertyFilterSlugs,
	parsePropertyFiltersUrl
} from '~/adapters/propertyFilters/propertyFiltersUrl';
import { getAppliedPropertyFilterKeys } from '~/core/entities/property/filterDefinitions';
import {
	createPropertyLocationFilters,
	DEFAULT_LOCATION_SLUG,
	preparePropertySearchFilters
} from '~/core/entities/property/filterPolicy';
import { type Ref } from 'vue';

import { createLabSearchActions } from './labActions';
import { createPropertySearchStore } from './store';
import { buildSearchRoute, buildSearchUrl, targetFor } from './url';

export function createPropertySearchScope(
	snapshot?: Ref<PropertySearchSnapshot>
): PropertySearchScope {
	const events = new Map<unknown, symbol>();
	const filtersApi: PropertyFiltersApi = {
		DEFAULT_URL_PATH: DEFAULT_PROPERTY_FILTERS_URL,
		DEFAULT_MAP_URL_PATH: `/venda/mapa-de-imoveis/${DEFAULT_LOCATION_SLUG}`,
		getPathFromFilters(filters, extraQueryStrings = {}, options = {}) {
			return buildSearchUrl(filters, { ...options, query: extraQueryStrings });
		},
		getFiltersFromPath(url, _previousFilters, page = 'imoveis') {
			return parsePropertyFiltersUrl({
				url,
				target: targetFor(page)
			});
		},
		getRouteObjectFromFilters(filters, { page, extraQueryStrings } = {}) {
			return buildSearchRoute(filters, { page, query: extraQueryStrings });
		},
		prepareFiltersForRequest(filters, options) {
			return preparePropertySearchFilters({ filters, ...options });
		},
		getAppliedFilterKeys: getAppliedPropertyFilterKeys,
		createLocationFilters: createPropertyLocationFilters,
		parseSlugPath: parsePropertyFilterSlugs,
		getPropertyTypeFromSlug,
		getSlugFromTransactionType,
		getTransactionTypeFromSlug
	};
	const search = createPropertySearchStore(snapshot);
	return {
		filtersApi,
		search,
		actions: createLabSearchActions(search),
		routeName: 'PropertiesListing',
		stateKey: key => `srp:${key}`,
		eventKey<T>(key: EventBusKey<T> | string): EventBusKey<T> {
			let scoped = events.get(key);
			if (!scoped) {
				scoped = Symbol(String(key));
				events.set(key, scoped);
			}
			return scoped;
		}
	};
}
